import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { FallbackStopLossPreview } from '../../api';
import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { formatMoney } from '../../components/format-money';

import { numericInputSx, OptionText, SettingsSection, SwitchRow } from './SettingsSection';
import type { RiskPreviewState } from './risk-preview-state';
import { type DraftIssue, needsFallbackStopLoss, type SettingsDraft } from './settings-draft';

/**
 * "Signals without stop loss" card (M2 R7, R8; mockups Reglages, ReglagesSansSL). Off unless the
 * trader turns it on. On + risk mode asks for a fallback distance with no default (Q2), and says
 * it only sizes the position: no stop loss is placed (FXM-02 §4, against the mockup's wording).
 * Since P9 it also says in money what the fallback distance means, from the form's preview.
 */
export function StopLossSection({
  draft,
  onChange,
  issues,
  preview,
}: {
  readonly draft: SettingsDraft;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
  readonly issues: readonly DraftIssue[];
  readonly preview: RiskPreviewState;
}): ReactElement {
  const { t } = useTranslation();
  const on = draft.copySignalsWithoutStopLoss;
  const fallbackIssue = issues.find((issue) => issue.field === 'fallbackStopLossPips');
  const fallbackExample = currentFallbackExample(preview);
  return (
    <SettingsSection title={t('settings.withoutStopLoss.title')}>
      <SwitchRow
        checked={on}
        onChange={(checked) => {
          onChange({ copySignalsWithoutStopLoss: checked });
        }}
        label={
          <OptionText
            title={t('settings.withoutStopLoss.label')}
            hint={on ? t('settings.withoutStopLoss.on') : t('settings.withoutStopLoss.off')}
          />
        }
      />
      {on && (
        <Alert severity="warning" sx={{ py: 0 }}>
          {t('settings.withoutStopLoss.warning')}
        </Alert>
      )}
      {needsFallbackStopLoss(draft) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box component="p" sx={{ m: 0, fontSize: 13, color: 'text.secondary' }}>
            {t('settings.withoutStopLoss.fallbackIntro')}
          </Box>
          <TextField
            label={t('settings.withoutStopLoss.fallbackLabel')}
            value={draft.fallbackStopLossPips}
            onChange={(event) => {
              onChange({ fallbackStopLossPips: event.target.value });
            }}
            error={fallbackIssue !== undefined}
            helperText={
              <>
                {fallbackIssue?.problem === 'required' &&
                  `${t('settings.withoutStopLoss.fallbackRequired')} `}
                {fallbackIssue?.problem === 'invalid' &&
                  `${t('settings.errors.fallbackStopLossPips')} `}
                {t('settings.withoutStopLoss.fallbackHelp')}
              </>
            }
            slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }}
            sx={numericInputSx}
          />
          <FallbackExample example={fallbackExample} />
        </Box>
      )}
    </SettingsSection>
  );
}

/**
 * The fallback example of a ready preview asked with a fallback distance, else `null` (P9 R4).
 * The preview state belongs to the current request only (`useRiskPreview`), so a ready one was
 * asked with the distance now in the form; a loading or failed one shows no amount (the sizing
 * card already says why), never the previous distance's.
 */
function currentFallbackExample(preview: RiskPreviewState): FallbackStopLossPreview | null {
  if (preview.status !== 'ready' || preview.fallbackStopLossPips === null) return null;
  // The amount must be for the distance asked: an answer for another distance shows nothing.
  const example = preview.preview.fallbackStopLoss;
  return example?.pips === preview.fallbackStopLossPips ? example : null;
}

/**
 * P9 R4–R5: "Without a stop, FXMate sizes as if the stop were 40 pips away: … you lose about
 * $125.00, your risk per trade." The field only shows in risk % mode (M2 R8), where this amount is
 * the risk budget by construction (the size adapts to the distance), so the text says so instead
 * of presenting it as a second, different figure. The amount is the API's, never computed here.
 */
function FallbackExample({
  example,
}: {
  readonly example: FallbackStopLossPreview | null;
}): ReactElement {
  const language = useLanguage();
  return (
    <Box aria-live="polite" sx={{ fontSize: 13, lineHeight: 1.5 }}>
      {example !== null && (
        <Trans
          i18nKey="settings.withoutStopLoss.fallbackExample"
          values={{
            pips: example.pips,
            amount: formatMoney({ ...example.estimatedLoss, language }),
          }}
          components={{ strong: <Box component="strong" sx={{ fontFamily: NUMERIC_FONT }} /> }}
        />
      )}
    </Box>
  );
}
